<script>
export default {
  props: {
    event: {
      type: Object,
      required: true
    }
  },
  computed: {
    audit () {
      return this.event.auditData || {}
    },
    risk () {
      return this.event.risk || { level: 'low', reasons: [] }
    },
    // The SharePoint payload carries an EventData blob of pseudo-XML such as
    // <PermissionsGranted>Leer</PermissionsGranted>. Turning it into pairs is
    // friendlier than dumping the raw string.
    eventData () {
      const raw = this.audit.EventData
      if (!raw || typeof raw !== 'string') return []
      const pairs = []
      raw.replace(/<([A-Za-z0-9_]+)>([\s\S]*?)<\/\1>/g, (match, tag, value) => {
        pairs.push({ key: this.camelCaseToSentence(tag), value: value })
        return match
      })
      return pairs
    },
    rawJson () {
      try {
        return JSON.stringify(this.event.rawRecord || this.event, null, 2)
      } catch (e) {
        return ''
      }
    }
  },
  methods: {
    camelCaseToSentence (text) {
      if (!text) return ''
      const result = String(text).replace(/([A-Z])/g, ' $1')
      return result.charAt(0).toUpperCase() + result.slice(1)
    },
    formatDate (value) {
      if (!value) return ''
      const parsed = new Date(value)
      return isNaN(parsed.getTime()) ? value : parsed.toLocaleString()
    },
    riskClass (level) {
      return {
        high: 'text-danger',
        medium: 'text-warning',
        low: 'text-success'
      }[level] || ''
    }
  }
}
</script>

<template>
  <div
    role="tablist"
    class="custom-accordionwitharrow"
  >
    <a
      v-b-toggle.collapse-sp-general
      href="#"
      class="text-dark"
    >
      <h5 class="mb-0">
        <feather
          type="chevron-down"
          class="align-middle"
        />
        General
      </h5>
    </a>
    <b-collapse
      id="collapse-sp-general"
      visible
    >
      <div class="custom-control">
        <b>Event Time:</b>&nbsp;{{ formatDate(event.eventDateTime) }}
      </div>
      <div class="custom-control">
        <b>Operation:</b>&nbsp;{{ camelCaseToSentence(event.operation) }}
      </div>
      <div class="custom-control">
        <b>Record Type:</b>&nbsp;{{ camelCaseToSentence(event.recordType) }}
      </div>
      <div class="custom-control">
        <b>Assessment:</b>&nbsp;
        <span :class="riskClass(risk.level)">{{ risk.level.toUpperCase() }}</span>
        <template v-if="risk.reasons && risk.reasons.length > 0">
          <ul class="mb-0 mt-1">
            <li
              v-for="(reason, index) in risk.reasons"
              :key="index"
            >
              {{ reason }}
            </li>
          </ul>
        </template>
      </div>
      <div class="custom-control">
        <b>User:</b>&nbsp;{{ event.userPrincipalName }}
      </div>
      <div class="custom-control">
        <b>Client IP:</b>&nbsp;{{ event.clientIp || 'N.A.' }}
      </div>
      <template v-if="audit.GeoLocation !== undefined">
        <div class="custom-control">
          <b>Geo Location:</b>&nbsp;{{ audit.GeoLocation }}
        </div>
      </template>
      <div class="custom-control">
        <b>Ingested At:</b>&nbsp;{{ formatDate(event.ingestedAt) }}
      </div>
      <div class="custom-control">
        <b>Event Id:</b>&nbsp;{{ event.eventId }}
      </div>
    </b-collapse>

    <a
      v-b-toggle.collapse-sp-resource
      href="#"
      class="text-dark"
    >
      <h5 class="mb-0 mt-2">
        <feather
          type="chevron-down"
          class="align-middle"
        />
        Resource
      </h5>
    </a>
    <b-collapse
      id="collapse-sp-resource"
      visible
    >
      <div class="custom-control">
        <b>Site:</b>&nbsp;
        <a
          v-if="event.siteUrl"
          :href="event.siteUrl"
          target="_blank"
          rel="noopener noreferrer"
        >{{ event.siteUrl }}</a>
        <span v-else>N.A.</span>
      </div>
      <div class="custom-control">
        <b>Object:</b>&nbsp;
        <a
          v-if="event.objectId"
          :href="event.objectId"
          target="_blank"
          rel="noopener noreferrer"
        >{{ event.objectId }}</a>
        <span v-else>N.A.</span>
      </div>
      <template v-if="audit.SourceRelativeUrl !== undefined">
        <div class="custom-control">
          <b>Relative Path:</b>&nbsp;{{ audit.SourceRelativeUrl }}
        </div>
      </template>
      <template v-if="audit.SourceFileName !== undefined">
        <div class="custom-control">
          <b>File Name:</b>&nbsp;{{ audit.SourceFileName }}
        </div>
      </template>
      <template v-if="audit.ItemType !== undefined">
        <div class="custom-control">
          <b>Item Type:</b>&nbsp;{{ audit.ItemType }}
        </div>
      </template>
    </b-collapse>

    <a
      v-b-toggle.collapse-sp-sharing
      href="#"
      class="text-dark"
    >
      <h5 class="mb-0 mt-2">
        <feather
          type="chevron-down"
          class="align-middle"
        />
        Sharing &amp; Permissions
      </h5>
    </a>
    <b-collapse
      id="collapse-sp-sharing"
      visible
    >
      <template v-if="audit.TargetUserOrGroupName !== undefined">
        <div class="custom-control">
          <b>Target:</b>&nbsp;{{ audit.TargetUserOrGroupName }}
          <span
            v-if="audit.TargetUserOrGroupType"
            class="text-muted"
          >({{ audit.TargetUserOrGroupType }})</span>
        </div>
      </template>
      <template v-if="audit.SharingLinkScope !== undefined">
        <div class="custom-control">
          <b>Link Scope:</b>&nbsp;{{ audit.SharingLinkScope }}
        </div>
      </template>
      <template v-if="audit.Permission !== undefined">
        <div class="custom-control">
          <b>Permission:</b>&nbsp;{{ audit.Permission }}
        </div>
      </template>
      <template v-for="pair in eventData">
        <div
          :key="pair.key"
          class="custom-control"
        >
          <b>{{ pair.key }}:</b>&nbsp;{{ pair.value }}
        </div>
      </template>
      <div
        v-if="eventData.length === 0 && audit.TargetUserOrGroupName === undefined"
        class="text-muted"
      >
        No sharing details on this event.
      </div>
    </b-collapse>

    <a
      v-b-toggle.collapse-sp-client
      href="#"
      class="text-dark"
    >
      <h5 class="mb-0 mt-2">
        <feather
          type="chevron-down"
          class="align-middle"
        />
        Client
      </h5>
    </a>
    <b-collapse id="collapse-sp-client">
      <template v-if="audit.BrowserName !== undefined">
        <div class="custom-control">
          <b>Browser:</b>&nbsp;{{ audit.BrowserName }} {{ audit.BrowserVersion }}
        </div>
      </template>
      <template v-if="audit.Platform !== undefined">
        <div class="custom-control">
          <b>Platform:</b>&nbsp;{{ audit.Platform }}
        </div>
      </template>
      <template v-if="audit.ApplicationDisplayName !== undefined">
        <div class="custom-control">
          <b>Application:</b>&nbsp;{{ audit.ApplicationDisplayName }}
        </div>
      </template>
      <template v-if="audit.AuthenticationType !== undefined">
        <div class="custom-control">
          <b>Authentication:</b>&nbsp;{{ audit.AuthenticationType }}
        </div>
      </template>
      <template v-if="audit.IsManagedDevice !== undefined">
        <div class="custom-control">
          <b>Managed Device:</b>&nbsp;
          <span :class="audit.IsManagedDevice ? 'text-success' : 'text-warning'">
            {{ audit.IsManagedDevice ? 'Yes' : 'No' }}
          </span>
        </div>
      </template>
      <template v-if="audit.DeviceDisplayName !== undefined">
        <div class="custom-control">
          <b>Device:</b>&nbsp;{{ audit.DeviceDisplayName }}
        </div>
      </template>
      <template v-if="audit.UserAgent !== undefined">
        <div class="custom-control">
          <b>User Agent:</b>&nbsp;<span class="text-muted">{{ audit.UserAgent }}</span>
        </div>
      </template>
    </b-collapse>

    <a
      v-b-toggle.collapse-sp-raw
      href="#"
      class="text-dark"
    >
      <h5 class="mb-0 mt-2">
        <feather
          type="chevron-down"
          class="align-middle"
        />
        Raw Record
      </h5>
    </a>
    <b-collapse id="collapse-sp-raw">
      <pre class="sp-raw-json">{{ rawJson }}</pre>
    </b-collapse>
  </div>
</template>

<style scoped>
  .sp-raw-json {
    max-height: 400px;
    overflow: auto;
    background-color: #f8f9fa;
    padding: 10px;
    font-size: 0.75rem;
  }
</style>
